import {
  clearHostConsentPreferences,
  setHostConsentPreferences
} from './consent';
import { isEmbedConsentContext } from './consentUx';
import './matchingIdentity';
import { getEmbedAuthorizationContext } from './embedAuthorization';

export const EMBED_CONSENT_CONTRACT_VERSION = 1;
export const EMBED_CONSENT_MESSAGE = 'lvm:consent';
export const EMBED_BRIDGE_READY_MESSAGE = 'lvm:bridge-ready';

const SESSION_ID_PATTERN = /^[A-Za-z0-9_-]{32,128}$/;
const IDENTIFIER_KEYS = ['fbc', 'fbp', 'ttclid', 'ttp', 'externalId'];

const normalizeOrigin = (value) => {
  if (!value) return '';
  try {
    const parsed = new URL(value);
    return parsed.origin === String(value).replace(/\/$/, '') ? parsed.origin : '';
  } catch (error) {
    return '';
  }
};

const normalizeSessionId = (value) => {
  const normalized = String(value || '').trim();
  return SESSION_ID_PATTERN.test(normalized) ? normalized : '';
};

const getImmediateAncestorOrigin = (ancestorOrigins) => {
  if (!ancestorOrigins) return '';
  try {
    const value = typeof ancestorOrigins.item === 'function'
      ? ancestorOrigins.item(0)
      : ancestorOrigins[0];
    return normalizeOrigin(value);
  } catch (error) {
    return '';
  }
};

export const resolveTrustedEmbedContext = ({
  location = typeof window !== 'undefined' ? window.location : {},
  referrer = typeof document !== 'undefined' ? document.referrer : '',
  ancestorOrigins = typeof window !== 'undefined' ? window.location?.ancestorOrigins : null,
  parentWindow = typeof window !== 'undefined' ? window.parent : null,
  currentWindow = typeof window !== 'undefined' ? window : null
} = {}) => {
  if (!isEmbedConsentContext(location) || !parentWindow || parentWindow === currentWindow) return null;

  const params = new URLSearchParams(location.search || '');
  const parentOrigin = normalizeOrigin(
    params.get('lvm_parent_origin') || params.get('parentOrigin')
  );
  const sessionId = normalizeSessionId(params.get('lvm_embed_session'));
  const referrerOrigin = normalizeOrigin((() => {
    try { return referrer ? new URL(referrer).origin : ''; } catch (error) { return ''; }
  })());

  // Keep browser-authored parent evidence separate from React Router's
  // location object. Safari Private Browsing can omit document.referrer but
  // still exposes the immediate frame ancestor through window.location.
  const ancestorOrigin = getImmediateAncestorOrigin(ancestorOrigins);

  if (!parentOrigin || !sessionId || (!referrerOrigin && !ancestorOrigin)) return null;
  if (referrerOrigin && referrerOrigin !== parentOrigin) return null;
  if (ancestorOrigin && ancestorOrigin !== parentOrigin) return null;
  const authorizationContext = getEmbedAuthorizationContext(location);
  return {
    version: EMBED_CONSENT_CONTRACT_VERSION,
    parentOrigin,
    sessionId,
    ...(authorizationContext?.origin === parentOrigin
      ? { authorization: authorizationContext.token }
      : {})
  };
};

export const normalizeHostConsentMessage = (data, context) => {
  if (!context || !data || typeof data !== 'object' || Array.isArray(data)) return null;
  if (data.type !== EMBED_CONSENT_MESSAGE || Number(data.version) !== EMBED_CONSENT_CONTRACT_VERSION) return null;
  if (data.sessionId !== context.sessionId) return null;
  if (!['pending', 'granted', 'denied', 'notice'].includes(data.status)) return null;

  const categories = data.categories && typeof data.categories === 'object'
    ? data.categories
    : {};
  if (typeof categories.analytics !== 'boolean' || typeof categories.marketing !== 'boolean') return null;
  const isNotice = data.status === 'notice' && data.basis === 'notice_opt_out'
    && categories.marketing === true && categories.analytics === false;
  if (data.status === 'notice' && !isNotice) return null;
  if (data.status !== 'granted' && !isNotice && (categories.analytics || categories.marketing)) return null;

  const decisionId = String(data.decisionId || '').trim().slice(0, 120);
  const decidedAt = String(data.decidedAt || '').trim();
  if (data.status !== 'pending' && (!decisionId || !Number.isFinite(Date.parse(decidedAt)))) return null;

  const identifiers = {};
  if ((data.status === 'granted' || isNotice) && categories.marketing && data.identifiers && typeof data.identifiers === 'object') {
    IDENTIFIER_KEYS.forEach((key) => {
      const value = String(data.identifiers[key] || '').trim();
      if (value) identifiers[key] = value.slice(0, 255);
    });
  }

  return {
    status: data.status,
    ...(isNotice ? { basis: 'notice_opt_out' } : {}),
    categories: {
      analytics: data.status === 'granted' && categories.analytics,
      marketing: (data.status === 'granted' || isNotice) && categories.marketing
    },
    decisionId,
    decidedAt: data.status === 'pending' ? null : new Date(decidedAt).toISOString(),
    identifiers,
    matchingIdentity: categories.marketing ? window.LVMMatchingIdentity?.normalize(data.matchingIdentity) || undefined : undefined
  };
};

export const startEmbedConsentBridge = ({ location } = {}) => {
  if (typeof window === 'undefined') return () => {};
  if (!isEmbedConsentContext(location || window.location)) {
    clearHostConsentPreferences();
    return () => {};
  }

  const context = resolveTrustedEmbedContext({ location: location || window.location });
  setHostConsentPreferences({ status: 'pending', categories: { analytics: false, marketing: false } });
  if (!context) return () => clearHostConsentPreferences();

  const handleMessage = (event) => {
    if (event.source !== window.parent || event.origin !== context.parentOrigin) return;
    const consent = normalizeHostConsentMessage(event.data, context);
    if (!consent) return;
    // Set the snapshot before the permission event wakes cart/checkout effects.
    if (consent.categories.marketing && consent.matchingIdentity) window.LVMMatchingIdentity?.remember(consent.matchingIdentity);
    setHostConsentPreferences(consent);
  };

  const publishIdentity = () => {
    const matchingIdentity = window.LVMMatchingIdentity?.get();
    if (matchingIdentity) window.parent.postMessage({type:'lvm:matching-identity', version:1, sessionId:context.sessionId, matchingIdentity}, context.parentOrigin);
  };
  const publishDetails = event => {
    const signal = event.detail;
    if (!/^checkout_details_[a-f0-9]{24}$/.test(signal?.eventId || '') || !signal?.payload) return;
    window.parent.postMessage({type:'lvm:checkout-details',version:1,sessionId:context.sessionId,
      signal,matchingIdentity:window.LVMMatchingIdentity?.get() || undefined},context.parentOrigin);
  };
  window.addEventListener('lvm:checkout-details-completed', publishDetails);
  window.addEventListener('lvm:matching-identity-updated', publishIdentity);
  window.addEventListener('message', handleMessage);
  window.parent.postMessage({
    type: EMBED_BRIDGE_READY_MESSAGE,
    version: EMBED_CONSENT_CONTRACT_VERSION,
    sessionId: context.sessionId
  }, context.parentOrigin);

  return () => {
    window.removeEventListener('message', handleMessage);
    window.removeEventListener('lvm:matching-identity-updated', publishIdentity);
    window.removeEventListener('lvm:checkout-details-completed', publishDetails);
    clearHostConsentPreferences();
  };
};
